<template>
  <div class='integrations-layout'>
    <div class='integrations-layout__header'>
      <div class='integrations-layout__header__title'>
        {{ $t('integration.layout.title') }}
      </div>
      <div class='integrations-layout__header__add-new-btn'>
        <v-btn class="new-integration-button" color="primary" @click="newIntegrationClicked">
          {{ $t('integration.layout.add.new.integration.btn') }}
        </v-btn>
      </div>
    </div>
    <div class='integrations-layout__search'>
      <filtered-search
        @searchInput="submitSearchFilter"
        :show-filters="false"
        :searchItem='search'
      />
    </div>
    <div class='integrations-layout__content'>
      <wombat-table
        :items="integrationsList"
        :columns="tableColumns"
        :resize="false"
        :loadingItems="true"
        :columnsReorder="false"
        :itemHeight="58"
        :noResultsMessage="$t('noResultsFound')"
        @columnSorted="columnSorted"
      >
        <component
          slot="row-cell"
          slot-scope="rowCell"
          class="row-cell"
          :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
          :item="rowCell.item"
          :column="rowCell.column"
          @editIntegrationConfig='editIntegrationConfig'
          @deleteIntegration='openDeleteIntegrationPopup'
        />
      </wombat-table>
    </div>
    <edit-integration-details-popup
      :visible-popup='showEditIntegrationDetailsPopup'
      @closed='showEditIntegrationDetailsPopup = false'
      @save='saveIntegration'
    />
    <yes-no-popup
      :visible-popup='displayDeleteIntegrationPopup'
      title="delete.integration.popup.title"
      message="delete.integration.popup.message"
      yesCaption="delete.integration.popup.yes"
      noCaption="delete.integration.popup.no"
      :loading="isDeletingIntegration"
      @clicked='deleteIntegrationPopupClicked'
    />
  </div>
</template>

<script>
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import {
  CHANGE_INTEGRATIONS_LIST_OPTIONS, DELETE_INTEGRATION,
  SAVE_INTEGRATION,
  SET_INTEGRATION_DETAILS_FOR_EDIT,
} from '@/store/integration/actionTypes';
import { mapGetters } from 'vuex';
import { getEmptyColumn, integrationsColumns } from '@/services/tableColumns';
import IntegrationCreatedDateTableCell from '@/components/tableCells/integrationsTableCells/IntegrationCreatedDateTableCell.vue';
import IntegrationIconTableCell from '@/components/tableCells/integrationsTableCells/IntegrationIconTableCell.vue';
import IntegrationActionTableCell from '@/components/tableCells/integrationsTableCells/IntegrationActionTableCell.vue';
import { views } from '@/router/constants';
import EditIntegrationDetailsPopup from '@/components/popup/EditIntegrationDetailsPopup.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';

export default {
  name: 'IntegrationsLayout',
  components: {
    FilteredSearch, EditIntegrationDetailsPopup, WombatTable, YesNoPopup,
  },
  data() {
    return {
      search: '',
      showEditIntegrationDetailsPopup: false,
      displayDeleteIntegrationPopup: false,
      integrationObjectToDelete: null,
      isDeletingIntegration: false,
    };
  },
  methods: {
    async openDeleteIntegrationPopup(integrationObject) {
      this.integrationObjectToDelete = integrationObject;
      this.displayDeleteIntegrationPopup = true;
    },
    async deleteIntegrationAction() {
      await this.$store.dispatch(DELETE_INTEGRATION, { integrationId: this.integrationObjectToDelete.id });
    },
    async deleteIntegrationPopupClicked(value) {
      if (value) {
        this.isDeletingIntegration = true;
        try {
          await this.deleteIntegrationAction();
        } finally {
          this.isDeletingIntegration = false;
        }
      }
      this.displayDeleteIntegrationPopup = false;
      this.integrationObjectToDelete = null;
    },
    decodeIcon(base64Icon) {
      const buffDecoded = Buffer.from(base64Icon, 'base64');
      return buffDecoded.toString('ascii');
    },
    async saveIntegration(integrationData) {
      await this.$store.dispatch(SAVE_INTEGRATION, integrationData);
      this.showEditIntegrationDetailsPopup = false;
    },
    async editIntegrationConfig(integration) {
      await this.$store.dispatch(SET_INTEGRATION_DETAILS_FOR_EDIT, integration);
      this.showEditIntegrationDetailsPopup = true;
    },
    async clearSearch() {
      this.search = '';
      await this.$store.dispatch(CHANGE_INTEGRATIONS_LIST_OPTIONS, {
        search: this.search,
        page: 1,
        searchUpdated: true,
      });
    },
    async submitSearchFilter(e) {
      this.search = e.trim();
      if (this.search.length) {
        await this.$store.dispatch(CHANGE_INTEGRATIONS_LIST_OPTIONS, {
          search: this.search,
          searchUpdated: false,
          page: 1,
        });
      } else {
        await this.clearSearch();
      }
    },
    newIntegrationClicked() {
      this.$router.push({ name: views.ADD_INTEGRATION });
    },
    translateCategory(category) {
      return this.$t(`integration.category.${category.toLowerCase()}`);
    },
    async columnSorted({ column, direction }) {
      await this.$store.dispatch(CHANGE_INTEGRATIONS_LIST_OPTIONS, {
        page: this.page,
        limit: this.itemsPerPage,
        search: this.search,
        sort: direction
          ? {
            [column.sortingField || column.field]: direction,
          }
          : {},
      });
    },
  },
  async mounted() {
    await this.$store.dispatch(CHANGE_INTEGRATIONS_LIST_OPTIONS, {
      page: 1,
      limit: this.itemsPerPage,
      turnOnLoading: true,
      search: '',
    });
  },
  computed: {
    ...mapGetters(['integrations', 'integrationListSort', 'integrationDetailsToEdit']),
    integrationsList() {
      return this.integrations.map(integration => ({
        id: integration.id,
        date: integration.createdDate,
        category: this.translateCategory(integration.category),
        name: integration.name,
        displayName: integration.metaData.displayName,
        metaData: integration.metaData,
        icon: integration.metaData.icon ? this.decodeIcon(integration.metaData.icon) : '',
        conditionOptions: integration.conditionOptions || [],
        conditions_available: integration.metaData.conditions_available === undefined ? true : integration.metaData.conditions_available,
      }));
    },
    tableColumns() {
      const columns = [
        integrationsColumns.icon,
        integrationsColumns.name,
        integrationsColumns.category,
        integrationsColumns.date,
        integrationsColumns.actions,
      ]
        .filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
          sort: this.integrationListSort[column.sortingField || column.field],
        }));

      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    tableRowCellComponentsHash() {
      return {
        actions: IntegrationActionTableCell,
        icon: IntegrationIconTableCell,
        date: IntegrationCreatedDateTableCell,
        default: WombatRowDefaultCell,
      };
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";

.integrations-layout {
  padding: $space-8;

  &__search {
    width: rem(600);
    margin-left: $space-4;
    margin-top: $space-3;
    margin-bottom: $space-6;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__title {
      margin-bottom: $space-8;
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-2xl;
      font-weight: bold;
      line-height: $line-height-tight;
      letter-spacing: $letter-spacing-normal;
    }

    &__add-new-btn {
      justify-self: right;

      .new-integration-button.v-btn.v-btn.v-btn {
        display: inline-block;
        cursor: pointer;
        height: $btn-height-md;
        width: rem(140);
        border-radius: $radius-sm;
        background-color: var(--brand-primary);
        color: #ffffff;
        font-size: $font-size-base;
        font-family: $font-family-medium;
        letter-spacing: $letter-spacing-wide;
        line-height: $line-height-tight;
        text-align: center;
        text-transform: uppercase;

        &:hover {
          opacity: 0.9;
        }
      }
    }
  }

  &__content {
    :deep(.wombat-table) {
      --displayactioncell: none;
      padding: $space-4 0;

      .wombat-header {
        padding-bottom: $space-2;
      }

      .cell {
        z-index: $z-base + 10;
      }

      .wombat-row.wombat-row {
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom: $border-width-thin solid var(--border-primary);
      }

      .wombat-overlay {
        &:hover {
          background-color: var(--bg-tertiary);
          --display: block;
          --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, var(--bg-tertiary) 100%);
          --displayactioncell: flex;
        }
      }
    }
  }
}
</style>
